import { useMutation } from '@apollo/client/react';
import { Card, Checkbox, Col, Row } from 'antd';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMedia } from 'react-use';
import { BREAKPOINTS, DESELECT_MESSAGE } from '../../../../../common/constants';
import CommonTooltip from '../../../../../components/CommonTooltip';
import EllipsisText from '../../../../../components/EllipsisText';
import { UPDATE_EQC_STAGE } from '../../../graphql/Mutation';
import './StageItemList.less';
import StageItemTable from './StageItemTable';

const StageItemList = ({
  stageId,
  stageData,
  eqcTypeData,
  refetchStageData,
  isStageSelectionActive,
  setIsStageItemSelectionActive,
}) => {
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isSelectAllAcrossPages, setIsSelectAllAcrossPages] = useState(false);
  const [exceptIds, setExceptIds] = useState([]);
  const [witnessRequired, setWitnessRequired] = useState(
    stageData?.witness || false,
  );
  const [drawingRequired, setDrawingRequired] = useState(
    stageData?.drawing || false,
  );

  // Refs for debouncing
  const witnessTimeoutRef = useRef(null);
  const drawingTimeoutRef = useRef(null);

  // UPDATE_EQC_STAGE mutation
  const [updateStage] = useMutation(UPDATE_EQC_STAGE, {
    onError() {},
    onCompleted() {
      if (refetchStageData) {
        refetchStageData();
      }
    },
  });

  // Debounced handler for witness checkbox
  const handleWitnessChange = useCallback(
    (checked) => {
      setWitnessRequired(checked);

      // Clear existing timeout
      if (witnessTimeoutRef.current) {
        clearTimeout(witnessTimeoutRef.current);
      }

      // Set new timeout for API call
      witnessTimeoutRef.current = setTimeout(() => {
        updateStage({
          variables: {
            id: stageId,
            data: {
              eqcTypeId: Number(eqcTypeData?.id),
              witness: checked,
            },
          },
        });
      }, 400);
    },
    [stageId, updateStage],
  );

  // Debounced handler for drawing checkbox
  const handleDrawingChange = useCallback(
    (checked) => {
      setDrawingRequired(checked);

      // Clear existing timeout
      if (drawingTimeoutRef.current) {
        clearTimeout(drawingTimeoutRef.current);
      }

      // Set new timeout for API call
      drawingTimeoutRef.current = setTimeout(() => {
        updateStage({
          variables: {
            id: stageId,
            data: {
              eqcTypeId: Number(eqcTypeData?.id),
              drawing: checked,
            },
          },
        });
      }, 400);
    },
    [stageId, updateStage],
  );

  // Update local state when stageData changes (stage-wise initialization)
  useEffect(() => {
    setWitnessRequired(stageData?.witness || false);
    setDrawingRequired(stageData?.drawing || false);
  }, [stageData?.witness, stageData?.drawing]);

  useEffect(() => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setIsSelectAllAcrossPages(false);
    setExceptIds([]);
  }, [stageId]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (witnessTimeoutRef.current) {
        clearTimeout(witnessTimeoutRef.current);
      }
      if (drawingTimeoutRef.current) {
        clearTimeout(drawingTimeoutRef.current);
      }
    };
  }, []);

  // Notify parent when stage item selection changes
  useEffect(() => {
    setIsStageItemSelectionActive?.(
      selectedRows?.length > 0 || isSelectAllAcrossPages,
    );
  }, [selectedRows, isSelectAllAcrossPages, setIsStageItemSelectionActive]);

  const handleClearSelection = () => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
    setIsSelectAllAcrossPages(false);
    setExceptIds([]);
  };

  const isAnyItemSelected = selectedRows?.length > 0 || isSelectAllAcrossPages;

  return (
    <div id="stage-item-list">
      <Card id="stage-item-card" className="stage-item-card">
        <div
          className={`header d-flex align-center justify-between ${
            isDesktopViewport ? 'mb-10' : ''
          } `}
        >
          <div id="stage-item-header" className="d-flex align-center">
            <h2 id="stage-items-title" className="mr-5">
              <EllipsisText
                text={stageData?.name}
                className="font-size-20"
                heightRows={2}
              />
            </h2>

            {/* Professional Selection Bar - Rendered via Portal from StageItemTable */}
            <div id="stage-item-selection-bar" />
          </div>

          <div
            id="stage-item-header-buttons"
            className={clsx(
              'eqc-stage-header-buttons d-flex',
              !isDesktopViewport && 'mb-10',
            )}
          >
            <div id="stageitem-add-btn" />
          </div>
        </div>

        <div id="undo-toast-portal" />

        <div className={clsx(isStageSelectionActive && 'stage-item-disabled')}>
          {isStageSelectionActive && (
            <CommonTooltip title={DESELECT_MESSAGE}>
              <div className="disabled-overlay" />
            </CommonTooltip>
          )}
          {/* Stage Requirements Inline Section */}
          <div className="stage-requirements-inline mb-10">
            <Row
              gutter={[24, 8]}
              className="stage-requirements-row"
              align="middle"
            >
              <Col xs={24} sm={8} md={6} lg={5}>
                <div className="stage-requirements-label">
                  Stage Requirements
                </div>
              </Col>
              <Col xs={12} sm={8} md={9} lg={9}>
                <CommonTooltip
                  title={
                    isStageSelectionActive || isAnyItemSelected
                      ? DESELECT_MESSAGE
                      : ''
                  }
                >
                  <Checkbox
                    checked={witnessRequired}
                    onChange={(e) => handleWitnessChange(e.target.checked)}
                    className="stage-requirement-checkbox"
                    disabled={isStageSelectionActive || isAnyItemSelected}
                  >
                    Witness Required
                  </Checkbox>
                </CommonTooltip>
              </Col>
              <Col xs={12} sm={8} md={9} lg={10}>
                <CommonTooltip
                  title={
                    isStageSelectionActive || isAnyItemSelected
                      ? DESELECT_MESSAGE
                      : ''
                  }
                >
                  <Checkbox
                    checked={drawingRequired}
                    onChange={(e) => handleDrawingChange(e.target.checked)}
                    className="stage-requirement-checkbox"
                    disabled={isStageSelectionActive || isAnyItemSelected}
                  >
                    Drawing Required
                  </Checkbox>
                </CommonTooltip>
              </Col>
            </Row>
          </div>

          <StageItemTable
            stageId={stageId}
            stageData={stageData}
            eqcTypeData={eqcTypeData}
            selectedRowKeys={selectedRowKeys}
            setSelectedRowKeys={setSelectedRowKeys}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            isSelectAllAcrossPages={isSelectAllAcrossPages}
            setIsSelectAllAcrossPages={setIsSelectAllAcrossPages}
            exceptIds={exceptIds}
            setExceptIds={setExceptIds}
            onClearSelection={handleClearSelection}
            isStageSelectionActive={isStageSelectionActive}
          />
        </div>
      </Card>
    </div>
  );
};

export default StageItemList;
